<template>
  <article class="blog-card" :class="{'blog-card--read': isInHistory}" @click="handleOpen">
    <div class="blog-card__image">
      <img v-if="item.image && !imageError" :src="item.image" :alt="title" loading="lazy" @error="imageError = true">
      <img v-else src="/assets/img/companies/huggingface_logo.svg" alt="" class="blog-card__placeholder">
    </div>

    <div class="blog-card__body">
      <h3 class="blog-card__title">{{ title }}</h3>
      <div class="blog-card__meta">
        <span class="blog-card__org">{{ org }}</span>
        <span class="blog-card__date">{{ date }}</span>
        <button
          @click.stop="shareCard"
          class="blog-card__btn"
          :title="showCopied ? '¡Enlace copiado!' : 'Compartir'"
        >
          <svg v-if="!showCopied" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="18" cy="5" r="3"/>
            <circle cx="6" cy="12" r="3"/>
            <circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </button>
        <button
          @click.stop="toggleFavorite"
          class="blog-card__btn"
          :class="{'blog-card__btn--active': isFavorite}"
          :title="isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
        </button>
      </div>
    </div>
  </article>
</template>

<script>
import bookmarksService from '../../../services/bookmarks.js';
import { copyShareUrl } from '../../../utils/shareUtils.js';

export default {
  name: 'BlogCard',
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      updateTrigger: 0,
      imageError: false,
      showCopied: false
    };
  },
  computed: {
    isFavorite() {
      this.updateTrigger;
      return bookmarksService.isFavorite(this.item.id || this.item.link);
    },
    isInHistory() {
      this.updateTrigger;
      return bookmarksService.isInHistory(this.item.id || this.item.link);
    },
    title() {
      // Algunos títulos del feed vienen con markdown (**negrita**)
      return (this.item.title || '').replace(/\*\*/g, '').trim();
    },
    org() {
      // huggingface.co/blog/{org}/{slug} -> org; huggingface.co/blog/{slug} -> blog oficial
      const parts = (this.item.link || '').split('/blog/')[1];
      const segments = parts ? parts.split('/').filter(Boolean) : [];
      return segments.length > 1 ? segments[0] : 'Hugging Face';
    },
    date() {
      const d = this.item.pubDate instanceof Date ? this.item.pubDate : new Date(this.item.pubDate);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' });
    }
  },
  methods: {
    handleOpen() {
      bookmarksService.addToHistory(this.item);
      window.open(this.item.link, '_blank', 'noopener,noreferrer');
    },
    async shareCard() {
      if (await copyShareUrl(this.item)) {
        this.showCopied = true;
        setTimeout(() => { this.showCopied = false; }, 2000);
      }
    },
    toggleFavorite() {
      if (this.isFavorite) {
        bookmarksService.removeFromFavorites(this.item.id || this.item.link);
      } else {
        bookmarksService.addToFavorites(this.item);
      }
    },
    handleBookmarkUpdate() {
      this.updateTrigger++;
    }
  },
  mounted() {
    window.addEventListener('bookmarks-updated', this.handleBookmarkUpdate);
  },
  beforeUnmount() {
    window.removeEventListener('bookmarks-updated', this.handleBookmarkUpdate);
  }
};
</script>

<style scoped>
.blog-card {
  display: flex;
  min-height: 150px;
  background: #16141f;
  border: 1px solid #2d333b;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.blog-card:hover {
  border-color: #4a4f5a;
  background: #1b1926;
}

.blog-card--read .blog-card__title {
  color: #a0aec0;
}

.blog-card__image {
  flex: 0 0 290px;
  background: #0e1217;
  display: flex;
  align-items: center;
  justify-content: center;
}

.blog-card__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blog-card__image .blog-card__placeholder {
  width: 64px;
  height: 64px;
  object-fit: contain;
  opacity: 0.5;
}

.blog-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  padding: 20px 24px 20px 32px;
}

.blog-card__title {
  margin: 0;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 23px;
  font-weight: 600;
  line-height: 1.35;
  color: #e1e7ef;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.blog-card__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
}

.blog-card__org {
  color: #4a9eff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.blog-card__date {
  margin-left: auto;
  color: #7d8694;
  white-space: nowrap;
}

.blog-card__btn {
  display: inline-flex;
  padding: 4px;
  background: transparent;
  border: none;
  color: #7d8694;
  cursor: pointer;
  transition: color 0.15s ease;
}

.blog-card__btn:hover {
  color: #e1e7ef;
}

.blog-card__btn--active {
  color: #ffb000;
}

.blog-card__btn--active svg {
  fill: currentColor;
}

@media (max-width: 768px) {
  .blog-card {
    flex-direction: column;
  }

  .blog-card__image {
    flex-basis: auto;
    aspect-ratio: 16 / 9;
  }

  .blog-card__body {
    padding: 16px;
    gap: 10px;
  }

  .blog-card__title {
    font-size: 18px;
  }
}
</style>
